import is from '@sindresorhus/is';

import type { EntityIdentifier } from '../../../data-contract/chat';
import {
    buildJobLevelParams,
    buildJobRoleParams,
} from '../../../job-architecture-v2/job-profile-standalone/hooks/use-locate-job-architecture-item';
import {
    createJsaClusterDetailTilePayload,
    createJsaFamilyDetailTilePayload,
    jobArchitectureUrlTileDefinitions,
} from '../../../job-architecture-v2/url-tiles';
import { writeUrlTileSearchParams } from '../../../url-tiles';

export type NavigationTarget = {
    readonly href: string;
    readonly openInNewTab: boolean;
};

const overviewHref = (architectureId: string, params: URLSearchParams): string =>
    `/job-architecture-v2/${architectureId}/overview?${params.toString()}`;

const entityVersion = (entity: EntityIdentifier): number | undefined =>
    is.number(entity.version) ? entity.version : undefined;

const buildRoleTarget = (entity: EntityIdentifier, architectureId: string): URLSearchParams | null => {
    if (!is.nonEmptyString(entity.id) || !is.nonEmptyString(entity.familyId) || !is.nonEmptyString(entity.clusterId)) {
        return null;
    }
    const path = { cluster: entity.clusterId, family: entity.familyId, role: entity.id };
    return buildJobRoleParams(architectureId, entity.id, path, new URLSearchParams(), undefined, entityVersion(entity));
};

const buildLevelTarget = (entity: EntityIdentifier, architectureId: string): URLSearchParams | null => {
    if (!is.nonEmptyString(entity.id) || !is.nonEmptyString(entity.familyId) || !is.nonEmptyString(entity.clusterId)) {
        return null;
    }
    if (!is.nonEmptyString(entity.roleId)) return null;
    const path = { cluster: entity.clusterId, family: entity.familyId, role: entity.roleId };
    return buildJobLevelParams(
        architectureId,
        entity.id,
        path,
        new URLSearchParams(),
        undefined,
        entityVersion(entity),
    );
};

const buildFamilyTarget = (entity: EntityIdentifier, architectureId: string): URLSearchParams | null => {
    if (!is.nonEmptyString(entity.id)) return null;
    return writeUrlTileSearchParams({
        definitions: jobArchitectureUrlTileDefinitions,
        payloads: [createJsaFamilyDetailTilePayload({ architectureId, familyId: entity.id, mode: 'view' })],
        searchParams: new URLSearchParams(),
    });
};

const buildClusterTarget = (entity: EntityIdentifier, architectureId: string): URLSearchParams | null => {
    if (!is.nonEmptyString(entity.id) || !is.nonEmptyString(entity.familyId)) return null;
    return writeUrlTileSearchParams({
        definitions: jobArchitectureUrlTileDefinitions,
        payloads: [
            createJsaClusterDetailTilePayload({
                architectureId,
                clusterId: entity.id,
                familyId: entity.familyId,
                mode: 'view',
            }),
        ],
        searchParams: new URLSearchParams(),
    });
};

// Builds the deep-link search params for a mention entity, or null when the
// entity is missing the parents required to locate it on the board. Mirrors the
// url-tile payloads used by the search modal navigation. Skill mentions never
// navigate: they open the in-chat skill details panel instead (see
// `SkillMention` in TextBlock).
const buildParams = (entity: EntityIdentifier, architectureId: string): URLSearchParams | null => {
    switch (entity.type) {
        case 'role':
            return buildRoleTarget(entity, architectureId);
        case 'level':
            return buildLevelTarget(entity, architectureId);
        case 'family':
            return buildFamilyTarget(entity, architectureId);
        case 'cluster':
            return buildClusterTarget(entity, architectureId);
        default:
            return null;
    }
};

export const toNavigationTarget = (entity: EntityIdentifier, openInNewTab: boolean): NavigationTarget | null => {
    if (!is.nonEmptyString(entity.jobArchitectureId)) return null;

    const params = buildParams(entity, entity.jobArchitectureId);
    if (is.null(params)) return null;

    return { href: overviewHref(entity.jobArchitectureId, params), openInNewTab };
};
